import {
    Box,
    Button,
    Image,
    Text,
    useColorMode,
    keyframes,
    Badge,
    Flex,
    HStack,
    VStack,
    useColorModeValue,
    Tooltip,
    useToast,
    IconButton,
    SimpleGrid,
} from "@chakra-ui/react";
import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect, useMemo, useRef } from "react";
import { BsGithub, BsBookmark, BsBookmarkFill, BsStars } from "react-icons/bs";
import {
    FiFolder, FiUser, FiUsers,
    FiExternalLink, FiX,
    FiEye, FiCopy, FiCheckCircle,
    FiChevronLeft, FiChevronRight,
} from "react-icons/fi";
import Bewakoof from "../Assets/Bewakoof.png";
import Healthcart from "../Assets/Healthcart.png";
import Eyekart from "../Assets/Eyekart.png";
import milkify from "../Assets/home-page-1.png";
import fileforge from "../Assets/fileforge.png";
import agrivision from "../Assets/agrivision.png";
import prescripto from "../Assets/prescripto.png";

/* ─── Ultra-Smooth Keyframe Animations ────────────────────────────── */
const fadeInDown = keyframes`
  0%   { opacity: 0; transform: translateY(-30px) scale(0.96); filter: blur(8px); }
  100% { opacity: 1; transform: translateY(0) scale(1); filter: blur(0px); }
`;

const cardCascade = keyframes`
  0%   { opacity: 0; transform: translateY(45px) scale(0.92); filter: blur(10px); }
  60%  { opacity: 0.9; transform: translateY(-4px) scale(1.01); filter: blur(2px); }
  100% { opacity: 1; transform: translateY(0) scale(1); filter: blur(0px); }
`;

const pulseOrb = keyframes`
  0%, 100% { opacity: 0.25; transform: scale(1) rotate(0deg); }
  50%      { opacity: 0.55; transform: scale(1.1) rotate(180deg); }
`;

const shimmerScan = keyframes`
  0%   { left: -130%; }
  100% { left: 150%; }
`;

const shimmerText = keyframes`
  0%   { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const borderRun = keyframes`
  0%   { background-position: 0% 50%; }
  50%  { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

/* ─── Per-Project Accent Palettes ───────────────────────────────── */
const ACCENTS = {
    0: { grad: "linear-gradient(135deg,#10b981,#06b6d4)", glow: "rgba(16,185,129,.35)", color: "#10b981" },
    1: { grad: "linear-gradient(135deg,#22c55e,#06b6d4)", glow: "rgba(34,197,94,.35)", color: "#22c55e" },
    2: { grad: "linear-gradient(135deg,#3b82f6,#6366f1)", glow: "rgba(99,102,241,.35)", color: "#3b82f6" },
    3: { grad: "linear-gradient(135deg,#f59e0b,#ef4444)", glow: "rgba(239,68,68,.35)", color: "#f59e0b" },
    4: { grad: "linear-gradient(135deg,#ec4899,#8b5cf6)", glow: "rgba(139,92,246,.35)", color: "#ec4899" },
    5: { grad: "linear-gradient(135deg,#14b8a6,#06b6d4)", glow: "rgba(6,182,212,.35)", color: "#14b8a6" },
    6: { grad: "linear-gradient(135deg,#a855f7,#6366f1)", glow: "rgba(168,85,247,.35)", color: "#a855f7" },
};

function Projects() {
    const { colorMode } = useColorMode();
    const isDark = colorMode === "dark";
    const toast = useToast();
    const spotlightRef = useRef(null);

    /* ── Color Tokens ── */
    const sectionBg = useColorModeValue("#f5f8ff", "#030712");
    const headerTitle = useColorModeValue("#0f172a", "#f1f5f9");
    const subText = useColorModeValue("#64748b", "#94a3b8");
    const cardBg = useColorModeValue("rgba(255,255,255,.94)", "rgba(8,14,30,.88)");
    const cardIdleBorder = useColorModeValue("rgba(203,213,225,.9)", "rgba(255,255,255,.08)");
    const titleColor = useColorModeValue("#0f172a", "#f1f5f9");
    const descColor = useColorModeValue("#475569", "#9ca3af");
    const techBg = useColorModeValue("#eff6ff", "rgba(59,130,246,.1)");
    const techColor = useColorModeValue("#1d4ed8", "#93c5fd");
    const techBorder = useColorModeValue("#bfdbfe", "rgba(59,130,246,.25)");
    const imgBg = useColorModeValue("#dde5f0", "#0d1526");
    const footerBorder = useColorModeValue("#e2e8f0", "rgba(255,255,255,.06)");
    const codeColor = useColorModeValue("#1e293b", "#cbd5e1");
    const codeBorder = useColorModeValue("#cbd5e1", "rgba(255,255,255,.14)");
    const codeHoverBg = useColorModeValue("#f1f5f9", "rgba(255,255,255,.06)");
    const badgeTypeBg = useColorModeValue("rgba(241,245,249,.95)", "rgba(255,255,255,.07)");
    const badgeTypeColor = useColorModeValue("#334155", "#cbd5e1");
    const badgeTypeBorder = useColorModeValue("#e2e8f0", "rgba(255,255,255,.1)");
    const numColor = useColorModeValue("rgba(99,102,241,.06)", "rgba(99,102,241,.07)");
    const scanColor = useColorModeValue("rgba(255,255,255,.55)", "rgba(255,255,255,.12)");
    const spotlightCardBg = useColorModeValue("rgba(255,255,255,0.97)", "rgba(10,18,38,0.95)");

    const overlayGrad = isDark
        ? "linear-gradient(to top, rgba(3,7,18,.92) 0%, rgba(3,7,18,.5) 55%, transparent 100%)"
        : "linear-gradient(to top, rgba(0,0,0,.62) 0%, rgba(0,0,0,.3) 55%, transparent 100%)";

    /* ── State ── */
    const [expanded, setExpanded] = useState({});
    const [hovered, setHovered] = useState(null);
    const [selectedProject, setSelectedProject] = useState(null);
    const [favorites, setFavorites] = useState(() => {
        try {
            const saved = localStorage.getItem("akshay_portfolio_fav_projects");
            return saved ? JSON.parse(saved) : [];
        } catch {
            return [];
        }
    });

    useEffect(() => {
        try {
            localStorage.setItem("akshay_portfolio_fav_projects", JSON.stringify(favorites));
        } catch (e) {
            console.error(e);
        }
    }, [favorites]);

    const openProjectSpotlight = (proj) => {
        if (selectedProject?.id === proj.id) {
            setSelectedProject(null);
            return;
        }
        setSelectedProject(proj);
        setTimeout(() => {
            if (spotlightRef.current) {
                spotlightRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
            }
        }, 50);
    };

    const navigateProject = (direction) => {
        if (!selectedProject) return;
        const currentIndex = projects.findIndex((p) => p.id === selectedProject.id);
        if (currentIndex === -1) return;
        let nextIndex = direction === "next" ? currentIndex + 1 : currentIndex - 1;
        if (nextIndex >= projects.length) nextIndex = 0;
        if (nextIndex < 0) nextIndex = projects.length - 1;
        setSelectedProject(projects[nextIndex]);
    };

    const toggleFavorite = (id, name, e) => {
        if (e) e.stopPropagation();
        setFavorites((prev) => {
            const exists = prev.includes(id);
            const updated = exists ? prev.filter((item) => item !== id) : [...prev, id];
            toast({
                title: exists ? "Removed from Favorites" : "Added to Favorites!",
                description: `${name} ${exists ? "removed from" : "saved in"} your bookmarked projects.`,
                status: exists ? "info" : "success",
                duration: 2500,
                isClosable: true,
                position: "bottom-right",
            });
            return updated;
        });
    };

    const copyToClipboard = (url, name, e) => {
        if (e) e.stopPropagation();
        navigator.clipboard.writeText(url);
        toast({
            title: "Link Copied!",
            description: `Live demo URL for ${name} copied to clipboard.`,
            status: "success",
            duration: 2500,
            isClosable: true,
            position: "bottom-right",
        });
    };

    const toggle = (id) => setExpanded((p) => ({ ...p, [id]: !p[id] }));

    /* ── Data ── */
    const projects = useMemo(() => [
        {
            id: 0,
            name: "AgriVision",
            img: agrivision,
            type: "Individual",
            featured: true,
            category: "AI & Intelligence Platform",
            techstack: ["Next.js 15", "React 19", "TypeScript", "Node.js", "Express", "MongoDB", "Gemini AI", "Tailwind CSS", "Shadcn/UI", "Socket.io", "TanStack Query", "Zustand", "Framer Motion"],
            desc: "An AI-powered agricultural intelligence platform designed to empower farmers with real-time crop disease detection and predictive weather analytics.",
            highlights: [
                "Real-time crop disease diagnosis powered by Google Gemini AI Vision",
                "Predictive weather forecast and soil nutrient analytics dashboard",
                "Live community Q&A and real-time chat powered by Socket.io",
                "Optimized state architecture with Zustand and TanStack Query"
            ],
            livelink: "https://agirivisiion.netlify.app",
            gitlink: "https://github.com/bakshay96/AgriVision",
        },
        {
            id: 1,
            name: "Milkify",
            img: milkify,
            type: "Individual",
            featured: true,
            category: "Dairy ERP Web Application",
            techstack: ["React", "Node.js", "Express", "MongoDB", "Chakra UI"],
            desc: "A comprehensive Milk Collection Management web application providing various functionalities for daily milk data management.",
            highlights: [
                "Daily milk collection register with automated rate calculation formulas",
                "Farmer member profile management, shift logs, and billing statements",
                "Interactive analytics dashboards styled using Chakra UI components"
            ],
            livelink: "https://milkify.netlify.app/",
            gitlink: "https://github.com/bakshay96/My-Dairy",
        },
        {
            id: 2,
            name: "FileForge",
            img: fileforge,
            type: "Individual",
            featured: true,
            category: "SaaS Utility Platform",
            techstack: ["Next.js", "TypeScript", "FastAPI", "Python", "MongoDB", "Tailwind CSS", "JWT Auth", "Pillow", "PyMuPDF"],
            desc: "A secure, full-stack file processing SaaS platform supporting image format conversion, resizing, editing, PDF compression, and PDF-to-image conversion. Features real MIME-type validation, EXIF stripping, and JWT-based authentication.",
            highlights: [
                "High-performance python backend (FastAPI, Pillow, PyMuPDF) for rapid media rendering",
                "Enterprise security with real file magic-byte validation and EXIF metadata stripping",
                "PDF to high-res image conversion and customizable image compression presets",
                "User authentication, token management, and quota tracking via JWT & MongoDB"
            ],
            livelink: "https://fileforgee1.netlify.app",
            gitlink: "https://github.com/bakshay96/FileForge",
        },
        {
            id: 3,
            name: "Prescripto Healthcare ERP",
            img: prescripto,
            type: "Individual",
            featured: true,
            category: "Healthcare & Pharmacy ERP",
            techstack: ["Next.js", "React 19", "TypeScript", "Python", "FastAPI", "MongoDB", "Tailwind CSS", "UX4G", "GraphQL", "Razorpay", "Recharts"],
            desc: "An enterprise-grade hospital OPD management & pharmacy retail ERP. Features mouse-free keyboard autocomplete, auto-detected dosage category badges, read-only unit price indicators, live patient expected bill estimator, single-page A4 print isolation, GST tax invoice generator, and financial revenue analytics.",
            highlights: [
                "Keyboard-first autocomplete workflow for ultra-fast clinical prescription entry",
                "Automated GST invoice generation with A4 print layout isolation rules",
                "Live expected patient bill estimation engine with real-time tax breakdowns",
                "GraphQL APIs for real-time inventory updates and financial dashboard analytics"
            ],
            livelink: "https://we-prescripto.netlify.app/",
            gitlink: "https://github.com/bakshay96/Prescripto",
        },
        {
            id: 4,
            name: "Bewakoof Clone",
            img: Bewakoof,
            type: "Individual",
            featured: false,
            category: "E-Commerce App",
            techstack: ["React", "JavaScript", "HTML", "CSS"],
            desc: "A ReactJS e-commerce web application for buying shoes & clothes online with seamless shopping experience.",
            highlights: [
                "Product filter system by size, color, brand, and dynamic price range",
                "Interactive cart management with promo coupon validation",
                "Fully responsive design optimized for mobile and desktop screens"
            ],
            livelink: "https://bewakoofstore.netlify.app/",
            gitlink: "https://github.com/bakshay96/clone-bewakoof.com",
        },
        {
            id: 5,
            name: "Healthkart Clone",
            img: Healthcart,
            type: "Group",
            featured: true,
            category: "E-Commerce Platform",
            techstack: ["React", "Redux", "Chakra UI", "REST API"],
            desc: "An E-Commerce platform allowing users to buy health and wellness products with advanced filtering.",
            highlights: [
                "Global application state management using Redux Toolkit",
                "Comprehensive supplement catalog with category and price sorting",
                "Team project delivered collaboratively within a 5-day hackathon sprint"
            ],
            livelink: "https://vigor-orcin.vercel.app/",
            gitlink: "https://github.com/harry-io/healthkart.com",
        },
        {
            id: 6,
            name: "Eyekart Clone",
            img: Eyekart,
            type: "Group",
            featured: false,
            category: "E-Commerce Platform",
            techstack: ["React", "Node.js", "MongoDB", "Express", "Redux"],
            desc: "An online eyewear retailer offering glasses, contact lenses, and sunglasses for all age groups.",
            highlights: [
                "Full MERN stack implementation for custom eyewear ordering",
                "User account registration, order history, and address management",
                "Product recommendation engine based on frame shape and prescription type"
            ],
            livelink: "https://eyekart-sachin-kesarwani.vercel.app/",
            gitlink: "https://github.com/Sachin-Kesarwani/omniscient-dust-8885",
        },
    ], []);

    return (
        <Box id="projects" w="full" bg={sectionBg} position="relative" overflow="hidden" pt={{ base: 14, md: 18 }} pb={{ base: 6, md: 8 }}>

            {/* ── Dynamic Glowing Background Orbs ── */}
            {[
                { top: "-12%", right: "-8%", w: "700px", grad: "radial(rgba(99,102,241,.18),transparent 65%)", dur: "16s" },
                { bottom: "-8%", left: "-8%", w: "600px", grad: "radial(rgba(14,165,233,.15),transparent 65%)", dur: "12s" },
                { top: "42%", left: "38%", w: "450px", grad: "radial(rgba(168,85,247,.11),transparent 65%)", dur: "10s" },
            ].map((o, i) => (
                <Box key={i} position="absolute" {...(o.top ? { top: o.top } : {})} {...(o.bottom ? { bottom: o.bottom } : {})}
                    {...(o.right ? { right: o.right } : {})} {...(o.left ? { left: o.left } : {})}
                    w={o.w} h={o.w} bgGradient={o.grad} borderRadius="full"
                    filter="blur(110px)" pointerEvents="none"
                    animation={`${pulseOrb} ${o.dur} ease-in-out infinite`}
                />
            ))}

            <Box w="92%" maxW="1380px" m="auto" position="relative" zIndex={1}>

                {/* ════ Section Header ════════════════ */}
                <VStack spacing={3} align="center" textAlign="center" mb={12}
                    animation={`${fadeInDown} 0.8s cubic-bezier(0.16, 1, 0.3, 1) both`}
                >
                    {/* Eyebrow Label Pill */}
                    <Flex
                        align="center"
                        gap={2}
                        textTransform="uppercase"
                        border="1px solid"
                        borderColor={useColorModeValue("gray.300", "gray.600")}
                        fontWeight={700}
                        fontSize="xs"
                        letterSpacing="0.1em"
                        bg={useColorModeValue("blue.50", "rgba(59, 130, 246, 0.15)")}
                        color={useColorModeValue("blue.600", "blue.400")}
                        py={1.5}
                        px={4}
                        rounded="full"
                        boxShadow="sm"
                    >
                        <FiFolder style={{ width: "14px", height: "14px" }} />
                        My Projects
                    </Flex>

                    {/* Main Headline */}
                    <Text
                        fontSize={{ base: "3xl", md: "5xl", lg: "6xl" }}
                        fontWeight={900} color={headerTitle}
                        letterSpacing="-0.03em" lineHeight={1.1}
                    >
                        Featured{" "}
                        <Box as="span" position="relative" display="inline-block"
                            sx={{
                                background: "linear-gradient(135deg, #3b82f6 0%, #6366f1 50%, #a855f7 100%)",
                                backgroundSize: "200% auto",
                                WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
                                backgroundClip: "text",
                                animation: `${shimmerText} 4s linear infinite`,
                            }}
                        >
                            Projects
                        </Box>
                    </Text>

                    {/* Gradient Underline Accent Bar */}
                    <Box
                        width="60px"
                        height="4px"
                        bgGradient="linear(to-r, blue.400, purple.500)"
                        borderRadius="full"
                        my={1}
                    />

                    <Text fontSize={{ base: "sm", md: "md" }} color={subText} maxW="620px" lineHeight="1.8">
                        Explore full-stack applications, AI intelligence tools & SaaS solutions. Click any project card to view complete inline architecture details and live demos.
                    </Text>
                </VStack>

                {/* ════ Animated Spotlight Info Layer Over UI (Inline Over The Project Section) ════════════════ */}
                <AnimatePresence>
                    {selectedProject && (
                        <Box
                            ref={spotlightRef}
                            id="project-spotlight-layer"
                            as={motion.div}
                            key={`spotlight-${selectedProject.id}`}
                            initial={{ opacity: 0, height: 0, scale: 0.96, y: -20 }}
                            animate={{ opacity: 1, height: "auto", scale: 1, y: 0 }}
                            exit={{ opacity: 0, height: 0, scale: 0.96, y: -20 }}
                            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                            mb={12}
                            w="full"
                            position="relative"
                            zIndex={10}
                        >
                            <Box
                                bg={spotlightCardBg}
                                borderRadius="3xl"
                                border="2px solid"
                                borderColor={ACCENTS[selectedProject.id]?.color || "#2563eb"}
                                boxShadow={isDark
                                    ? "0 25px 70px rgba(0,0,0,0.85), 0 0 45px rgba(59,130,246,0.2)"
                                    : "0 25px 60px rgba(37,99,235,0.18)"}
                                p={{ base: 5, md: 8 }}
                                position="relative"
                                overflow="hidden"
                                backdropFilter="blur(32px)"
                            >
                                {/* Top Accent Gradient Bar */}
                                <Box h="5px" position="absolute" top={0} left={0} right={0} sx={{ background: ACCENTS[selectedProject.id]?.grad }} />

                                {/* Control Header: Spotlight Badge, Title & Navigation */}
                                <Flex direction={{ base: "column", md: "row" }} justify="space-between" align={{ base: "flex-start", md: "center" }} mb={6} gap={4}>
                                    <HStack spacing={3} wrap="wrap">
                                        <Badge
                                            bgGradient="linear(to-r, blue.500, purple.500)"
                                            color="white"
                                            px={3} py={1} borderRadius="full"
                                            fontSize="11px" fontWeight={800}
                                            display="flex" alignItems="center" gap={1.5}
                                            letterSpacing="0.05em"
                                        >
                                            <BsStars /> PROJECT SPOTLIGHT
                                        </Badge>
                                        <Badge bg={badgeTypeBg} color={badgeTypeColor} border="1px solid" borderColor={badgeTypeBorder} px={3} py={1} borderRadius="full" fontSize="11px" fontWeight={800}>
                                            {selectedProject.category}
                                        </Badge>
                                        <Badge colorScheme="purple" px={3} py={1} borderRadius="full" fontSize="11px" fontWeight={800}>
                                            {selectedProject.type} Project
                                        </Badge>
                                    </HStack>

                                    {/* Action Switchers: Prev / Next / Close */}
                                    <HStack spacing={2} alignSelf={{ base: "flex-end", md: "auto" }}>
                                        <Tooltip label="Previous Project" hasArrow>
                                            <IconButton
                                                aria-label="Previous Project"
                                                icon={<FiChevronLeft style={{ width: "18px", height: "18px" }} />}
                                                size="sm"
                                                borderRadius="xl"
                                                variant="outline"
                                                borderColor={codeBorder}
                                                color={titleColor}
                                                onClick={() => navigateProject("prev")}
                                                _hover={{ bg: codeHoverBg, transform: "scale(1.05)" }}
                                            />
                                        </Tooltip>
                                        <Tooltip label="Next Project" hasArrow>
                                            <IconButton
                                                aria-label="Next Project"
                                                icon={<FiChevronRight style={{ width: "18px", height: "18px" }} />}
                                                size="sm"
                                                borderRadius="xl"
                                                variant="outline"
                                                borderColor={codeBorder}
                                                color={titleColor}
                                                onClick={() => navigateProject("next")}
                                                _hover={{ bg: codeHoverBg, transform: "scale(1.05)" }}
                                            />
                                        </Tooltip>
                                        <Button
                                            size="sm"
                                            borderRadius="xl"
                                            colorScheme="red"
                                            variant="subtle"
                                            leftIcon={<FiX />}
                                            onClick={() => setSelectedProject(null)}
                                            fontWeight={800}
                                            fontSize="xs"
                                        >
                                            Close Info Layer
                                        </Button>
                                    </HStack>
                                </Flex>

                                {/* Main Details Body: 2 Columns for Desktop View */}
                                <SimpleGrid columns={{ base: 1, lg: 12 }} spacing={{ base: 6, lg: 8 }} align="flex-start">

                                    {/* Column 1: Image Showcase & Primary Actions (5 cols on Desktop) */}
                                    <Box gridColumn={{ lg: "span 5" }} w="full">
                                        <Box
                                            position="relative"
                                            h={{ base: "220px", sm: "280px", md: "320px" }}
                                            borderRadius="2xl"
                                            overflow="hidden"
                                            bg={imgBg}
                                            boxShadow="lg"
                                            mb={4}
                                        >
                                            <Image
                                                src={selectedProject.img}
                                                alt={selectedProject.name}
                                                w="full" h="full"
                                                objectFit="cover"
                                            />
                                            <Box position="absolute" inset={0} bg="linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 60%)" />
                                            {selectedProject.featured && (
                                                <Badge position="absolute" top={3} left={3} bg="linear-gradient(90deg,#f59e0b,#ef4444)" color="white" px={3} py={1} borderRadius="lg" fontSize="10px" fontWeight={900}>
                                                    ★ FEATURED SHOWCASE
                                                </Badge>
                                            )}
                                        </Box>

                                        {/* Desktop Action Buttons Bar */}
                                        <VStack spacing={3} w="full">
                                            <Flex gap={3} w="full">
                                                <Button
                                                    flex={1}
                                                    as="a"
                                                    target="_blank"
                                                    href={selectedProject.livelink}
                                                    size="md"
                                                    colorScheme="blue"
                                                    leftIcon={<FiExternalLink style={{ width: "16px", height: "16px" }} />}
                                                    borderRadius="xl"
                                                    fontWeight={800}
                                                    fontSize="xs"
                                                    sx={{ background: ACCENTS[selectedProject.id]?.grad }}
                                                    _hover={{ filter: "brightness(1.15)", transform: "translateY(-2px)" }}
                                                >
                                                    Live Demo
                                                </Button>
                                                <Button
                                                    flex={1}
                                                    as="a"
                                                    target="_blank"
                                                    href={selectedProject.gitlink}
                                                    size="md"
                                                    variant="outline"
                                                    leftIcon={<BsGithub style={{ width: "16px", height: "16px" }} />}
                                                    borderRadius="xl"
                                                    fontWeight={800}
                                                    fontSize="xs"
                                                    borderColor={codeBorder}
                                                    color={titleColor}
                                                    _hover={{ bg: codeHoverBg, transform: "translateY(-2px)" }}
                                                >
                                                    Source Code
                                                </Button>
                                            </Flex>

                                            <Flex gap={3} w="full">
                                                <Button
                                                    flex={1}
                                                    size="sm"
                                                    variant="outline"
                                                    leftIcon={<FiCopy />}
                                                    borderRadius="xl"
                                                    fontWeight={700}
                                                    fontSize="xs"
                                                    borderColor={codeBorder}
                                                    color={subText}
                                                    onClick={(e) => copyToClipboard(selectedProject.livelink, selectedProject.name, e)}
                                                >
                                                    Copy Live URL
                                                </Button>
                                                <Button
                                                    flex={1}
                                                    size="sm"
                                                    variant="outline"
                                                    leftIcon={favorites.includes(selectedProject.id) ? <BsBookmarkFill style={{ color: "#f59e0b" }} /> : <BsBookmark />}
                                                    borderRadius="xl"
                                                    fontWeight={700}
                                                    fontSize="xs"
                                                    borderColor={codeBorder}
                                                    color={subText}
                                                    onClick={(e) => toggleFavorite(selectedProject.id, selectedProject.name, e)}
                                                >
                                                    {favorites.includes(selectedProject.id) ? "Saved" : "Save Favorite"}
                                                </Button>
                                            </Flex>
                                        </VStack>
                                    </Box>

                                    {/* Column 2: Full Project Details & Key Highlights (7 cols on Desktop) */}
                                    <VStack gridColumn={{ lg: "span 7" }} align="flex-start" spacing={5} w="full">
                                        <Box w="full">
                                            <Text fontSize={{ base: "2xl", md: "3xl" }} fontWeight={900} color={titleColor} letterSpacing="-0.02em" mb={2}>
                                                {selectedProject.name}
                                            </Text>
                                            <Text fontSize="sm" color={descColor} lineHeight="1.85">
                                                {selectedProject.desc}
                                            </Text>
                                        </Box>

                                        {/* Key Architecture Highlights (Shown in one go) */}
                                        <Box w="full">
                                            <Text fontSize="xs" fontWeight={800} color={subText} textTransform="uppercase" letterSpacing="0.08em" mb={3}>
                                                ⚡ Key Architecture Highlights & Engineering Features
                                            </Text>
                                            <VStack align="flex-start" spacing={2.5} w="full">
                                                {selectedProject.highlights.map((highlight, idx) => (
                                                    <Flex
                                                        key={idx}
                                                        align="flex-start"
                                                        gap={3}
                                                        p={3}
                                                        bg={techBg}
                                                        borderRadius="2xl"
                                                        w="full"
                                                        border="1px solid"
                                                        borderColor={techBorder}
                                                        transition="transform 0.2s ease"
                                                        _hover={{ transform: "translateX(4px)" }}
                                                    >
                                                        <Flex
                                                            w={6} h={6} borderRadius="full"
                                                            align="center" justify="center"
                                                            bg="blue.500" color="white" flexShrink={0} mt="1px"
                                                        >
                                                            <FiCheckCircle style={{ width: "14px", height: "14px" }} />
                                                        </Flex>
                                                        <Text fontSize="xs" fontWeight={700} color={titleColor} lineHeight="1.6">
                                                            {highlight}
                                                        </Text>
                                                    </Flex>
                                                ))}
                                            </VStack>
                                        </Box>

                                        {/* Tech Stack Pills */}
                                        <Box w="full">
                                            <Text fontSize="xs" fontWeight={800} color={subText} textTransform="uppercase" letterSpacing="0.08em" mb={2.5}>
                                                🛠️ Tech Stack & Frameworks
                                            </Text>
                                            <Flex wrap="wrap" gap={2}>
                                                {selectedProject.techstack.map((tech, i) => (
                                                    <Badge
                                                        key={i}
                                                        px={3} py={1} borderRadius="lg"
                                                        fontSize="11px" fontWeight={700}
                                                        color={techColor} bg={techBg}
                                                        border="1px solid" borderColor={techBorder}
                                                        textTransform="none"
                                                    >
                                                        {tech}
                                                    </Badge>
                                                ))}
                                            </Flex>
                                        </Box>
                                    </VStack>
                                </SimpleGrid>
                            </Box>
                        </Box>
                    )}
                </AnimatePresence>

                {/* ════ Cards Grid Layout ═════════════════════════════ */}
                <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={{ base: 6, md: 7 }}>
                    {projects.map((proj, idx) => {
                        const isHov = hovered === proj.id;
                        const isSelected = selectedProject?.id === proj.id;
                        const accent = ACCENTS[proj.id] || ACCENTS[0];
                        const isFav = favorites.includes(proj.id);

                        return (
                            <Box key={proj.id}
                                position="relative"
                                animation={`${cardCascade} 0.65s cubic-bezier(0.16, 1, 0.3, 1) ${idx * 0.07}s both`}
                                onMouseEnter={() => setHovered(proj.id)}
                                onMouseLeave={() => setHovered(null)}
                                role="group"
                            >
                                {/* Outer Glow */}
                                <Box
                                    position="absolute" inset="-1px" borderRadius="26px"
                                    opacity={isHov || isSelected ? 0.35 : 0}
                                    transition="opacity .4s ease"
                                    zIndex={0} pointerEvents="none"
                                    sx={{
                                        background: `linear-gradient(135deg, ${accent.grad.replace("linear-gradient(135deg,", "").replace(")", "")})`,
                                        filter: `blur(20px)`,
                                    }}
                                />

                                {/* Animated Gradient Border Shell */}
                                <Box
                                    position="absolute" inset="-1.5px" borderRadius="27px"
                                    zIndex={0} pointerEvents="none"
                                    opacity={isHov || isSelected ? 1 : 0}
                                    transition="opacity .4s ease"
                                    sx={{
                                        background: `linear-gradient(135deg, ${accent.grad.replace("linear-gradient(135deg,", "").replace(")", "")}`,
                                        backgroundSize: "300% 300%",
                                        animation: isHov || isSelected ? `${borderRun} 3s ease infinite` : "none",
                                    }}
                                />

                                {/* Interactive Card Container */}
                                <Box
                                    bg={cardBg}
                                    backdropFilter="blur(28px)"
                                    borderRadius="26px"
                                    overflow="hidden"
                                    border="2px solid"
                                    borderColor={isSelected ? accent.color : isHov ? "transparent" : cardIdleBorder}
                                    position="relative"
                                    zIndex={1}
                                    display="flex" flexDirection="column"
                                    boxShadow={isHov || isSelected
                                        ? isDark
                                            ? `0 28px 70px rgba(0,0,0,.65), 0 0 0 1px rgba(99,102,241,.15), inset 0 0 0 1px rgba(255,255,255,.04)`
                                            : `0 28px 70px rgba(37,99,235,.14), 0 0 0 1px rgba(59,130,246,.18)`
                                        : isDark
                                            ? "0 8px 32px rgba(0,0,0,.5)"
                                            : "0 8px 32px rgba(0,0,0,.06)"}
                                    transform={isSelected ? "translateY(-6px)" : isHov ? "translateY(-10px) scale(1.01)" : "translateY(0) scale(1)"}
                                    transition="all .4s cubic-bezier(.165,.84,.44,1)"
                                >
                                    {/* Accent Top Line */}
                                    <Box h="3.5px" sx={{ background: accent.grad }} flexShrink={0} />

                                    {/* Image Viewport */}
                                    <Box
                                        position="relative" overflow="hidden"
                                        h="215px" bg={imgBg} flexShrink={0}
                                        cursor="pointer"
                                        onClick={() => openProjectSpotlight(proj)}
                                    >
                                        {/* Watermark Index Number */}
                                        <Text
                                            position="absolute" bottom="-14px" right="10px"
                                            fontSize="140px" fontWeight={900}
                                            color={numColor} lineHeight={1}
                                            userSelect="none" zIndex={1}
                                            fontFamily="'Courier New', monospace"
                                        >
                                            {String(proj.id + 1).padStart(2, "0")}
                                        </Text>

                                        {/* Project Image */}
                                        <Image
                                            w="100%" h="100%" objectFit="cover"
                                            src={proj.img} alt={proj.name}
                                            transition="transform .7s cubic-bezier(.165,.84,.44,1)"
                                            transform={isHov ? "scale(1.08)" : "scale(1)"}
                                            zIndex={2} position="relative"
                                        />

                                        {/* Interactive Bookmark Button (Top Right) */}
                                        <Tooltip label={isFav ? "Remove Favorite" : "Save Favorite"} hasArrow>
                                            <IconButton
                                                aria-label="Favorite project"
                                                icon={isFav ? <BsBookmarkFill style={{ color: "#f59e0b" }} /> : <BsBookmark />}
                                                position="absolute" top={3} right={3}
                                                zIndex={7}
                                                size="sm"
                                                borderRadius="full"
                                                bg="rgba(15, 23, 42, 0.75)"
                                                color="white"
                                                backdropFilter="blur(10px)"
                                                _hover={{ transform: "scale(1.15)", bg: "rgba(15, 23, 42, 0.9)" }}
                                                onClick={(e) => toggleFavorite(proj.id, proj.name, e)}
                                            />
                                        </Tooltip>

                                        {/* Shimmer Scan Line */}
                                        <Box
                                            position="absolute" top={0} bottom={0}
                                            w="60%" zIndex={4} pointerEvents="none"
                                            sx={{
                                                background: `linear-gradient(105deg,transparent 40%,${scanColor} 50%,transparent 60%)`,
                                                animation: isHov
                                                    ? `${shimmerScan} .8s ease-out forwards`
                                                    : "none",
                                                left: "-130%",
                                            }}
                                        />

                                        {/* Dynamic Gradient Overlay */}
                                        <Box
                                            position="absolute" inset={0} zIndex={3}
                                            pointerEvents="none"
                                            sx={{ background: overlayGrad }}
                                            opacity={isHov ? 1 : 0.6}
                                            transition="opacity .4s ease"
                                        />

                                        {/* Quick View Floating Trigger Overlay */}
                                        <Flex
                                            position="absolute" inset={0} zIndex={5}
                                            align="center" justify="center"
                                            opacity={isHov ? 1 : 0}
                                            transition="opacity 0.3s ease"
                                            bg="rgba(0,0,0,0.35)"
                                            backdropFilter="blur(3px)"
                                        >
                                            <Button
                                                size="sm"
                                                borderRadius="full"
                                                colorScheme="blue"
                                                leftIcon={<FiEye />}
                                                shadow="lg"
                                                fontWeight={800}
                                                onClick={(e) => { e.stopPropagation(); openProjectSpotlight(proj); }}
                                                transform={isHov ? "scale(1)" : "scale(0.9)"}
                                                transition="all 0.3s ease"
                                            >
                                                {isSelected ? "Active Spotlight" : "Quick View Details"}
                                            </Button>
                                        </Flex>

                                        {/* Featured Corner Ribbon */}
                                        {proj.featured && (
                                            <Box
                                                position="absolute" top={0} left={0}
                                                zIndex={6} overflow="hidden"
                                                w="72px" h="72px" pointerEvents="none"
                                            >
                                                <Box
                                                    position="absolute"
                                                    top="16px" left="-20px"
                                                    px={6} py="2px"
                                                    transform="rotate(-45deg)"
                                                    sx={{ background: "linear-gradient(90deg,#f59e0b,#ef4444)" }}
                                                    color="white"
                                                    fontSize="7px" fontWeight={900}
                                                    letterSpacing="0.08em"
                                                    boxShadow="0 2px 8px rgba(0,0,0,.3)"
                                                >
                                                    ★ TOP
                                                </Box>
                                            </Box>
                                        )}
                                    </Box>

                                    {/* Card Content Body */}
                                    <Box p={{ base: 5, md: 5 }} display="flex" flexDirection="column" flexGrow={1}>

                                        {/* Category & Type Row */}
                                        <Flex align="center" justify="space-between" mb={2}>
                                            <Text fontSize="11px" fontWeight={800} color={accent.color} textTransform="uppercase" letterSpacing="0.05em">
                                                {proj.category}
                                            </Text>
                                            <Badge
                                                bg={badgeTypeBg} color={badgeTypeColor}
                                                border="1px solid" borderColor={badgeTypeBorder}
                                                px={2.5} py={0.5} borderRadius="lg"
                                                fontSize="9px" fontWeight={800}
                                                letterSpacing="0.06em" flexShrink={0}
                                                display="flex" alignItems="center" gap={1}
                                            >
                                                {proj.type === "Group" ? (
                                                    <FiUsers style={{ width: "10px", height: "10px" }} />
                                                ) : (
                                                    <FiUser style={{ width: "10px", height: "10px" }} />
                                                )}
                                                {proj.type.toUpperCase()}
                                            </Badge>
                                        </Flex>

                                        {/* Title */}
                                        <Text
                                            fontSize={{ base: "lg", md: "xl" }}
                                            fontWeight={800} color={titleColor}
                                            letterSpacing="-0.02em" lineHeight={1.2}
                                            mb={2}
                                            cursor="pointer"
                                            _hover={{ color: "blue.400" }}
                                            onClick={() => openProjectSpotlight(proj)}
                                        >
                                            {proj.name}
                                        </Text>

                                        {/* Description */}
                                        <Box mb={4} flexGrow={1}>
                                            <Text
                                                fontSize="xs" color={descColor}
                                                lineHeight="1.8"
                                                noOfLines={expanded[proj.id] ? undefined : 3}
                                            >
                                                {proj.desc}
                                            </Text>
                                            {proj.desc && proj.desc.length > 110 && (
                                                <Button
                                                    size="xs" variant="link"
                                                    color={isDark ? "blue.400" : "blue.600"}
                                                    fontWeight={700} fontSize="10px"
                                                    onClick={(e) => { e.stopPropagation(); toggle(proj.id); }}
                                                    _hover={{ textDecoration: "none", color: isDark ? "blue.300" : "blue.800" }}
                                                    mt={1} p={0} h="auto"
                                                >
                                                    {expanded[proj.id] ? "Show Less ▲" : "Read More ▼"}
                                                </Button>
                                            )}
                                        </Box>

                                        {/* Tech Stack Pills */}
                                        <Flex wrap="wrap" gap={1.5} mb={5}>
                                            {proj.techstack.map((tech, i) => (
                                                <Badge
                                                    key={i}
                                                    fontSize="10px" color={techColor}
                                                    bg={techBg} border="1px solid" borderColor={techBorder}
                                                    px={2.5} py={0.5} borderRadius="md"
                                                    fontWeight={600} textTransform="none"
                                                >
                                                    {tech}
                                                </Badge>
                                            ))}
                                        </Flex>

                                        {/* Footer Action Buttons */}
                                        <Box pt={4} borderTop="1px solid" borderColor={footerBorder}>
                                            <Flex gap={2} align="center">
                                                {/* Quick View Details Button */}
                                                <Button
                                                    flex={1.2}
                                                    size="sm" borderRadius="xl"
                                                    fontSize="xs" fontWeight={800}
                                                    colorScheme="blue"
                                                    leftIcon={<FiEye style={{ width: "14px", height: "14px" }} />}
                                                    onClick={() => openProjectSpotlight(proj)}
                                                    _hover={{ transform: "translateY(-2px)" }}
                                                >
                                                    {isSelected ? "Viewing Info" : "Quick View"}
                                                </Button>

                                                {/* Live Demo Button */}
                                                <Button
                                                    flex={1} as="a" target="_blank" href={proj.livelink}
                                                    leftIcon={<FiExternalLink style={{ width: "14px", height: "14px" }} />}
                                                    size="sm" borderRadius="xl"
                                                    fontSize="xs" fontWeight={700}
                                                    color="white"
                                                    sx={{ background: accent.grad }}
                                                    _hover={{
                                                        filter: "brightness(1.15)",
                                                        transform: "translateY(-2px)",
                                                        boxShadow: `0 8px 24px ${accent.glow}`,
                                                    }}
                                                    _active={{ transform: "scale(0.97)" }}
                                                    transition="all .25s ease"
                                                >
                                                    Demo
                                                </Button>

                                                {/* Source Code Button */}
                                                <Tooltip label="GitHub Repository" hasArrow placement="top">
                                                    <IconButton
                                                        aria-label="GitHub Repository"
                                                        as="a" target="_blank" href={proj.gitlink}
                                                        icon={<BsGithub style={{ width: "14px", height: "14px" }} />}
                                                        size="sm" borderRadius="xl"
                                                        variant="outline"
                                                        borderColor={codeBorder}
                                                        color={codeColor}
                                                        _hover={{ bg: codeHoverBg, transform: "translateY(-2px)" }}
                                                    />
                                                </Tooltip>
                                            </Flex>
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                        );
                    })}
                </SimpleGrid>

            </Box>
        </Box>
    );
}

export default Projects;
